import { useCallback, useEffect, useRef, useState } from "react";
import { getEmissionFactorBreakdown } from "./emissionFactorBreakdownApi";

export const PAGE_SIZE = 50;

const LOAD_ERROR = "Could not load emission factors";

const EMPTY_TOTALS = Object.freeze({ factors: 0, gas_wise: 0, fuel_energy: 0 });

/**
 * @param {{ isSuccess?: boolean, data?: { data?: object, error?: string } } | undefined} res an apiCall response
 * @returns {import("./emissionFactorBreakdownApi").BreakdownPage|null} the page, or null when the call failed
 */
export const pageOf = (res) => {
  const page = res?.data?.data;
  return res?.isSuccess === false || !Array.isArray(page?.kpis) ? null : page;
};

/**
 * The breakdown for the current filters, a page of KPIs at a time. A filter
 * change starts again from the first page; loadMore appends the next one. A
 * response that arrives after a newer request was sent is dropped, so fast
 * typing never shows stale rows.
 *
 * @param {import("./emissionFactorBreakdownApi").BreakdownFilters} filters
 * @returns {{ kpis: import("./emissionFactorBreakdownApi").BreakdownKpi[], totalKpis: number, totals: { factors: number, gas_wise: number, fuel_energy: number }, loading: boolean, loadingMore: boolean, error: string, reload: () => Promise<void>, loadMore: () => Promise<void> }}
 */
export function useEmissionFactorBreakdown(filters) {
  const [kpis, setKpis] = useState([]);
  const [totalKpis, setTotalKpis] = useState(0);
  const [totals, setTotals] = useState(EMPTY_TOTALS);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const fetchIdRef = useRef(0);
  const { search, scope, databaseId, method } = filters;

  const fetchPage = useCallback(
    async (offset) => {
      const fetchId = ++fetchIdRef.current;
      const first = offset === 0;
      if (first) setLoading(true);
      else setLoadingMore(true);
      setError("");
      try {
        const res = await getEmissionFactorBreakdown({ search, scope, databaseId, method }, { limit: PAGE_SIZE, offset });
        if (fetchId !== fetchIdRef.current) return;
        const page = pageOf(res);
        if (!page) {
          if (first) setKpis([]);
          setError(res?.data?.error || LOAD_ERROR);
          return;
        }
        setKpis((current) => (first ? page.kpis : [...current, ...page.kpis]));
        setTotalKpis(page.total_kpis);
        setTotals(page.totals);
      } catch {
        if (fetchId !== fetchIdRef.current) return;
        if (first) setKpis([]);
        setError(LOAD_ERROR);
      } finally {
        if (fetchId === fetchIdRef.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [search, scope, databaseId, method],
  );

  const reload = useCallback(() => fetchPage(0), [fetchPage]);
  const loadMore = useCallback(() => fetchPage(kpis.length), [fetchPage, kpis.length]);

  useEffect(() => {
    reload();
  }, [reload]);

  return { kpis, totalKpis, totals, loading, loadingMore, error, reload, loadMore };
}
